'use client';

import { useCallback, useMemo, useState } from 'react';

import { doc, firestore, setDoc } from '@ps/functions/firebase/firestore';
import {
  type PopStoreExtendedSubscription,
  PopStoreExtendedSubscriptionLevel,
  PopStoreSubscriptionLevel,
  type Subscriptions,
} from '@ps/models/lib/creator-subscription';

import {
  isActiveSubscription,
  subscriptionsToList,
} from '../functions/access-control';
import { getSubscriptionDisplayName } from '../functions/subscription-display';
import {
  setupSubscriptionConfig,
  updateSubscriptionConfig,
} from '../functions/subscriptionApis';
import type {
  SubscriptionConfigurationInfo,
  SubscriptionConfigurationPayloadBase,
} from '../types';

export const kDefaultCommitmentPeriod = '6';

export const kDefaultFollowersDescription =
  "Follow me here to get regular updates on my life, my latest updates, my coolest content and more. Don't let the algorithm get in between us. 😍";

const kDefaultSubsDescription =
  "Unlock exclusive content, behind-the-scenes drops, and direct access to my DM. Let's build something amazing together.";

export interface UseSubscriptionConfigurationFormOptions {
  /** All sibling tiers — needed for duplicate-name validation. */
  subscriptions: Subscriptions | undefined;
  /** Creator id (used for the FOLLOWER firestore write). */
  sellerId: string | undefined;
  minSubscriptionPrice?: number;
  maxSubscriptionPrice?: number;
}

export interface SubscriptionConfigurationFormResult {
  value: SubscriptionConfigurationInfo;
  /** Field-level error messages — empty string when valid. */
  errors: Partial<Record<keyof SubscriptionConfigurationInfo, string>>;
  setValue: <K extends keyof SubscriptionConfigurationInfo>(
    key: K,
    value: SubscriptionConfigurationInfo[K],
  ) => void;
  /** Sets `commitmentDiscount` and auto-derives `isLongerCommitment`.
   *  1:1 with legacy `setCommitmentDiscount` helper. */
  setCommitmentDiscount: (next: number | undefined) => void;
  /** `true` when every field passes its validator. */
  isValid: boolean;
  /** Persists the form via REST (paid tiers) or firestore (FOLLOWER). */
  save: () => Promise<void>;
  /** Returns the same map as `errors` but freshly recomputed. */
  validate: () => Partial<Record<keyof SubscriptionConfigurationInfo, string>>;
}

/**
 * Form controller for a single subscription tier. 1:1 with legacy
 * `useSubscriptionConfigurationForm`. Validation rules:
 *
 *   - title:         non-empty, 3-14 chars, no duplicates across
 *                    tiers (paid tiers only; FOLLOWER is unvalidated).
 *   - description:   non-empty.
 *   - monthlyPrice:  paid tiers only; must respect min / max prices.
 *   - commitment*:   when `isLongerCommitment` is true, percent must
 *                    be 1-100.
 *
 * `save()` decides which endpoint to call:
 *   - FOLLOWER → firestore `profiles/{sellerId}` merge of
 *     `followersSettings`.
 *   - Configured paid tier → `updateSubscriptionConfig` (PUT).
 *   - New paid tier → `setupSubscriptionConfig` (POST).
 */
export function useSubscriptionConfigurationForm(
  extendedSubscription: PopStoreExtendedSubscription,
  options: UseSubscriptionConfigurationFormOptions,
): SubscriptionConfigurationFormResult {
  const {
    subscriptions,
    sellerId,
    minSubscriptionPrice,
    maxSubscriptionPrice,
  } = options;

  const isFollower =
    extendedSubscription.level === PopStoreExtendedSubscriptionLevel.FOLLOWER;

  const subscription_data =
    isFollower || !isActiveSubscription(extendedSubscription.subscription)
      ? undefined
      : extendedSubscription.subscription.subscription_data;

  const isVip =
    !isFollower &&
    extendedSubscription.subscription.platform_sub_level ===
      PopStoreSubscriptionLevel.VIP;

  const initial = useMemo<SubscriptionConfigurationInfo>(() => {
    const price = subscription_data?.creator_subscription_price
      ?.creator_sub_price_without_precision
      ? subscription_data.creator_subscription_price
          .creator_sub_price_without_precision / 100
      : undefined;

    const defaultTitle = isFollower
      ? 'Follower'
      : (subscription_data?.creator_subscription?.creator_sub_name ??
        getSubscriptionDisplayName(
          isActiveSubscription(extendedSubscription.subscription)
            ? extendedSubscription.subscription
            : undefined,
          extendedSubscription.subscription.platform_sub_level,
        ));

    const defaultDescription = isFollower
      ? (extendedSubscription.description ?? kDefaultFollowersDescription) ||
        kDefaultFollowersDescription
      : (subscription_data?.creator_subscription?.creator_sub_description ??
        kDefaultSubsDescription);

    return {
      title: defaultTitle,
      description: defaultDescription,
      monthlyPrice: price,
      commitment: subscription_data?.creator_subscription_price
        ?.creator_sub_discount_commitment_period
        ? subscription_data.creator_subscription_price.creator_sub_discount_commitment_period.toString()
        : kDefaultCommitmentPeriod,
      commitmentDiscount:
        subscription_data?.creator_subscription_price
          ?.creator_sub_commitment_discount_percentage ?? undefined,
      isLongerCommitment: Boolean(
        subscription_data?.creator_subscription_price
          ?.discount_for_longer_subscription_commitment,
      ),
      primaryImage:
        subscription_data?.creator_subscription?.creator_sub_primary_image ??
        '',
      previewVideo:
        subscription_data?.creator_subscription?.creator_sub_preview_video ??
        '',
      backgroundImage:
        subscription_data?.creator_subscription?.creator_sub_background_image ??
        '',
      active: isFollower
        ? extendedSubscription.active !== false
        : (subscription_data?.creator_subscription.active ?? true),
    };
  }, [isFollower, subscription_data, extendedSubscription]);

  const [value, setValueState] =
    useState<SubscriptionConfigurationInfo>(initial);

  // 1:1 with legacy `useForm`'s per-field `interacted` flag —
  // inline field errors only render after the user has touched
  // the field, or after `validate()` has been called from
  // `handleSave`. This prevents pristine fields with pre-
  // populated (but technically out-of-bounds) values from
  // flashing a red error when the modal opens.
  const [interacted, setInteracted] = useState<
    Partial<Record<keyof SubscriptionConfigurationInfo, boolean>>
  >({});

  const setValue = useCallback(
    <K extends keyof SubscriptionConfigurationInfo>(
      key: K,
      next: SubscriptionConfigurationInfo[K],
    ) => {
      setValueState(prev => ({ ...prev, [key]: next }));
      setInteracted(prev => (prev[key] ? prev : { ...prev, [key]: true }));
    },
    [],
  );

  // Auto-couple `commitmentDiscount` ↔ `isLongerCommitment`.
  // 1:1 with legacy `setCommitmentDiscount` helper in
  // `SubscriptionSetupField` — typing a number into the discount
  // input flips the longer-commitment flag on; clearing it flips
  // the flag off. There is no separate UI toggle in the legacy.
  const setCommitmentDiscount = useCallback((next: number | undefined) => {
    setValueState(prev => ({
      ...prev,
      commitmentDiscount: next,
      isLongerCommitment: next != null,
    }));
    setInteracted(prev =>
      prev.commitmentDiscount ? prev : { ...prev, commitmentDiscount: true },
    );
  }, []);

  // Force-flip all fields to interacted. Called by `save()` so
  // pristine-but-invalid fields surface their error after a
  // submit attempt. 1:1 with legacy `form.validate()`'s side-
  // effect of flipping every control to `validated`.
  const markAllInteracted = useCallback(() => {
    setInteracted({
      title: true,
      description: true,
      monthlyPrice: true,
      commitment: true,
      commitmentDiscount: true,
      isLongerCommitment: true,
      primaryImage: true,
      previewVideo: true,
      backgroundImage: true,
      active: true,
    });
  }, []);

  // Validate the current `value`. Returns a key→message map; an
  // empty string means valid for that field.
  const validate = useCallback((): Partial<
    Record<keyof SubscriptionConfigurationInfo, string>
  > => {
    const errs: Partial<Record<keyof SubscriptionConfigurationInfo, string>> =
      {};

    // title — FOLLOWER skips title validation entirely.
    if (!isFollower) {
      const trimmed = value.title?.trim() ?? '';
      if (!trimmed) {
        errs.title = 'Please enter a tier name.';
      } else if (trimmed.length < 3) {
        errs.title = 'Character limit between 2 and 14.';
      } else if (trimmed.length > 14) {
        errs.title = 'Tier name must be 14 characters or less.';
      } else if (subscriptions) {
        const dup = subscriptionsToList(subscriptions).find(
          sub =>
            sub.platform_sub_level !==
              extendedSubscription.subscription?.platform_sub_level &&
            isActiveSubscription(sub) &&
            sub.subscription_data?.creator_subscription?.creator_sub_name
              ?.trim()
              .toLowerCase() === trimmed.toLowerCase(),
        );
        if (dup) {
          errs.title =
            'This tier name is already used by another subscription level.';
        }
      }
    }

    // description — required for all tiers, max 160 characters.
    const desc = value.description?.trim() ?? '';
    if (!desc) {
      errs.description = 'Please describe what subscribers will get.';
    } else if (desc.length >= 160) {
      errs.description = 'Only max 160 characters are allowed.';
    }

    // monthlyPrice — paid tiers only.
    if (!isFollower) {
      const p = value.monthlyPrice;
      if (p == null || Number.isNaN(p)) {
        errs.monthlyPrice = 'Please enter a subscription price.';
      } else if (p <= 0) {
        errs.monthlyPrice = 'Price must be > 0';
      } else if (minSubscriptionPrice != null && p < minSubscriptionPrice) {
        errs.monthlyPrice = `Price must be $${minSubscriptionPrice.toFixed(
          2,
        )} or higher.`;
      } else if (maxSubscriptionPrice != null && p > maxSubscriptionPrice) {
        errs.monthlyPrice = `Price must be $${maxSubscriptionPrice.toFixed(
          2,
        )} or lower to stay below the next tier.`;
      }
    }

    // commitmentDiscount — required when `isLongerCommitment` is on.
    if (value.isLongerCommitment) {
      const d = value.commitmentDiscount;
      if (d == null) {
        errs.commitmentDiscount = 'Discount is required for longer commitments';
      } else if (Number.isNaN(d) || d < 1 || d > 100) {
        errs.commitmentDiscount =
          'Discount percentage must be between 1 and 100';
      }
    }

    return errs;
  }, [
    isFollower,
    value,
    subscriptions,
    extendedSubscription,
    minSubscriptionPrice,
    maxSubscriptionPrice,
  ]);

  // Raw validator output — every rule violation. Drives
  // `isValid` (the save-button gate) and is returned by
  // `validate()` for imperative pre-save checks.
  const rawErrors = useMemo(() => validate(), [validate]);

  // User-facing errors — only render a message once the user
  // has interacted with that field (or `save()` flipped
  // everything to interacted). Pristine fields stay silent.
  // 1:1 with the `errorMsg` gating legacy form-control inputs
  // do via `interacted`.
  const errors = useMemo<
    Partial<Record<keyof SubscriptionConfigurationInfo, string>>
  >(() => {
    const out: Partial<Record<keyof SubscriptionConfigurationInfo, string>> =
      {};
    for (const [key, msg] of Object.entries(rawErrors)) {
      if (msg && interacted[key as keyof SubscriptionConfigurationInfo]) {
        out[key as keyof SubscriptionConfigurationInfo] = msg;
      }
    }
    return out;
  }, [rawErrors, interacted]);

  // Save-button gate uses raw validation (not interacted-
  // gated) — pristine invalid forms remain un-savable.
  const isValid = Object.values(rawErrors).every(msg => !msg);

  const save = useCallback(async () => {
    // Flip every field to interacted so latent errors surface
    // on the rendered form even if the user never typed into
    // them. Matches legacy `form.validate()`'s side effect of
    // marking each control as validated.
    markAllInteracted();

    if (!isValid) {
      throw new Error(
        Object.values(rawErrors).find(Boolean) ?? 'Invalid form state',
      );
    }

    // FOLLOWER → firestore merge on `profiles/{sellerId}`.
    if (isFollower) {
      if (!sellerId) throw new Error('Missing seller id');
      const db = firestore();
      await setDoc(
        doc(db, 'profiles', sellerId),
        {
          followersSettings: {
            description: value.description,
            active: value.active ?? true,
          },
        },
        { merge: true },
      );
      return;
    }

    // Paid tier → REST.
    const subscription = extendedSubscription.subscription;
    const creator_sub_price = Math.round((value.monthlyPrice ?? 0) * 100);

    const base: SubscriptionConfigurationPayloadBase = {
      creator_sub_name: value.title,
      creator_sub_description: value.description,
      creator_sub_primary_image: value.primaryImage || null,
      creator_sub_background_image: value.backgroundImage || null,
      creator_sub_preview_video: value.previewVideo || null,
      creator_sub_price,
      creator_sub_price_currency: 'USD',
      // Only VIP gets the longer-commitment discount option in the
      // legacy UI; collapse the toggle on non-VIP tiers regardless of
      // what the form state says.
      discount_for_longer_subscription_commitment: isVip
        ? value.isLongerCommitment
        : false,
      creator_sub_commitment_discount_percentage: value.isLongerCommitment
        ? (value.commitmentDiscount ?? 0)
        : undefined,
      creator_sub_discount_commitment_period: value.isLongerCommitment
        ? parseInt(value.commitment ?? kDefaultCommitmentPeriod, 10) ||
          Number(kDefaultCommitmentPeriod)
        : undefined,
      active: value.active ?? true,
    };

    if (isActiveSubscription(subscription)) {
      await updateSubscriptionConfig({
        ...base,
        creator_sub_id:
          subscription.subscription_data.creator_subscription.creator_sub_id,
      });
    } else {
      if (!sellerId) throw new Error('Missing seller id');
      await setupSubscriptionConfig({
        ...base,
        platform_sub_level_id: subscription.platform_sub_level_id,
        creator_store_id: sellerId,
      });
    }
  }, [
    isFollower,
    isVip,
    isValid,
    rawErrors,
    markAllInteracted,
    sellerId,
    value,
    extendedSubscription,
  ]);

  return {
    value,
    errors,
    setValue,
    setCommitmentDiscount,
    isValid,
    save,
    validate,
  };
}
